import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Bundle } from 'fhir/r4';
import { map, Observable } from 'rxjs';
import { ApiService } from '../../api';
import { ArrivalBoardConfiguration } from '../entities/arrival-board-configuration';
import { ArrivalBoardItem } from '../entities/arrival-board-item';
import { ArrivalBoardDataSource } from './arrival-board-data-source';
import { mapBundleToItems } from './fhir-bundle.mapper';
import { joinUrl } from './join-url';

/**
 * URI template of the board endpoint, used when the HAL root does not advertise the link.
 * The HAL href is the same template — `HypermediaLink` carries no `templated` flag, so the
 * substitution happens here either way.
 */
const BOARD_PATH_TEMPLATE = 'arrival-board/{boardId}';

/** Placeholder substituted with the board uuid. */
const BOARD_ID_PLACEHOLDER = '{boardId}';

/**
 * HTTP-backed source of ArrivalBoard rows.
 *
 * One poll is one `GET arrival-board/{boardId}`, which streams back a raw FHIR searchset; all row
 * shaping lives in {@link mapBundleToItems}. `Content-Type: application/fhir+json` needs no
 * handling — `HttpClient` parses JSON regardless of the media type.
 *
 * Two deliberate non-features:
 *
 * - **Errors are not caught here.** The kiosk page distinguishes permanent failures (`403`/`404`,
 *   stop polling) from transient ones (`5xx`/offline, keep the last rows), which needs the status.
 * - **The {@link ArrivalBoardSimulator} methods are not implemented**, so the kiosk's dev control
 *   panel — gated on duck-typing the datasource — disappears by itself in production.
 */
@Injectable()
export class HttpArrivalBoardDataSource extends ArrivalBoardDataSource {
    private readonly http = inject(HttpClient);
    private readonly apiService = inject(ApiService);

    fetch(config: ArrivalBoardConfiguration): Observable<ArrivalBoardItem[]> {
        return this.http
            .get<Bundle>(this.boardUrl(config.uuid))
            .pipe(map((bundle) => mapBundleToItems(bundle, config.columns)));
    }

    /**
     * Resolved per call, never in a field initializer: a field would freeze the URL at construction
     * time, which is before `ApiService.init()` has necessarily resolved the API root.
     *
     * @param boardId - uuid of the polled board
     */
    private boardUrl(boardId: string): string {
        const href = this.apiService.rootResponseSignal()?._links?.arrivalBoard?.href ?? BOARD_PATH_TEMPLATE;
        return joinUrl(this.apiService.urlSignal(), href.replace(BOARD_ID_PLACEHOLDER, boardId));
    }
}
